import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleSafeRepricing: React.FC = () => {
  const BASE = '/images/help/safe-repricing';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/sr-step-1.png`, alt: 'Safe repricing overview', caption: 'Step 1 – How safe repricing protects you from extreme Amazon price swings' },
    ],
    step2: [
      { src: `${BASE}/sr-step-2.png`, alt: 'Safe repricing protection in action', caption: 'Step 2 – What safe repricing does when it detects an unusual change' },
    ],
    step3: [
      { src: `${BASE}/sr-step-3.png`, alt: 'Enable safe repricing settings', caption: 'Step 3 – Enable safe repricing in your pricing settings' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Safe Repricing Protection | CodeRom Help"
        description="How CodeRom's safety buffers prevent selling below cost during repricing."
      />
      <HelpBreadcrumbSchema slug="safe-repricing" name="Safe repricing protection" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Safe repricing protection</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Safe repricing protection</h1>
                <p className="article-subtitle">
                  Protect your listings from sudden Amazon price spikes or drops. Safe repricing adds an extra layer of security to your automated pricing.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 5 min read</span>
                  <span className="category">Pricing Protection</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. What is safe repricing?</a></li>
                  <li><a href="#step2">2. How it protects you</a></li>
                  <li><a href="#step3">3. Enable safe repricing</a></li>
                  <li><a href="#step4">4. Set your threshold</a></li>
                  <li><a href="#step5">5. What happens when protection triggers</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: What is safe repricing?</h2>
                  <p>
                    Amazon prices can change suddenly — a product might spike from £20 to £200 due to a listing error, or drop to £1 during a flash sale. Without protection, these extreme prices would automatically update your eBay listing, potentially causing you to sell at a massive loss or price yourself out of the market.
                  </p>
                  <p>
                    Safe repricing is your safety net. When a supplier price moves more than you are comfortable with, it takes the listing off sale rather than letting the unusual price reach your buyers — and puts it back automatically once the price settles.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: What is safe repricing?" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: How it protects you</h2>
                  <p>
                    Every few hours Coderom looks at the supplier prices it has recorded for a product over the monitoring period (3 days by default). It takes the highest and the lowest of those prices and works out how far apart they are, as a percentage of the lowest. That single figure is what your threshold is compared against.
                  </p>
                  <p>
                    This is a <strong>range</strong>, not a direction. A price that climbed and a price that fell produce the same figure, so a £10 supplier price is treated the same whether it moved to £16 or down to £6.25. There is one threshold, and it covers both.
                  </p>
                  <p>
                    If that figure goes over your threshold, Coderom:
                  </p>
                  <ol>
                    <li><strong>Takes the listing off sale</strong> — the eBay quantity is set to 0, so nothing can be bought at a price based on volatile data</li>
                    <li><strong>Leaves the listing itself in place</strong> — it is not ended or deleted, and it keeps its history and watchers</li>
                    <li><strong>Records why</strong> — the product shows a <strong>Safe Repricing</strong> badge with the measured change, and appears under the Safe Repricing filter in Products</li>
                    <li><strong>Puts it back on sale by itself</strong> once the price has settled — you do not have to approve anything</li>
                  </ol>
                  <div className="tip-box">
                    <div className="tip-icon">🛡️</div>
                    <div className="tip-content">
                      <strong>Why take it off sale instead of just holding the price?</strong> eBay treats a cancellation for "out of stock" as a transaction defect, and defects are capped at 2% of your orders. Setting the quantity to 0 is eBay's own out-of-stock option and does not count against you, so pausing sales is the safer of the two.
                    </div>
                  </div>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: How it protects you" />
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Enable safe repricing</h2>
                  <ol>
                    <li>Go to <strong>Settings → Pricing</strong></li>
                    <li>Find <strong>Safe Repricing Protection</strong></li>
                    <li>Toggle it <strong>On</strong></li>
                    <li>Click <strong>Save</strong></li>
                  </ol>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Enable safe repricing" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> We strongly recommend keeping safe repricing enabled at all times. It prevents costly pricing mistakes with zero effort.
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Set your threshold</h2>
                  <p>
                    There is a single threshold, plus two timing settings. All three live in <strong>Settings → Pricing → Safe Repricing Protection</strong>.
                  </p>

                  <div className="threshold-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Price Change Threshold</strong></td>
                          <td>How far the supplier price may range before the listing comes off sale. Allowed values are 20 to 80.</td>
                          <td>60% (default)</td>
                        </tr>
                        <tr>
                          <td><strong>Monitoring Period</strong></td>
                          <td>How far back Coderom looks when measuring that range.</td>
                          <td>3 days (default)</td>
                        </tr>
                        <tr>
                          <td><strong>Stability Period</strong></td>
                          <td>How long to wait before checking whether the price has settled.</td>
                          <td>1 day (default)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p>
                    Presets are offered next to the field, and you can type any value between 20 and 80:
                  </p>
                  <ul>
                    <li><strong>30% — Sensitive.</strong> Catches ordinary supplier movement; expect a noticeable share of your catalogue to be off sale at any time.</li>
                    <li><strong>40% — Balanced.</strong></li>
                    <li><strong>60% — Recommended.</strong> The default. Ignores everyday price movement and reacts to genuine spikes.</li>
                    <li><strong>80% — Relaxed.</strong> Only the most extreme changes take a listing off sale.</li>
                  </ul>
                  <p>
                    Two worked examples at the default threshold of 60%:
                  </p>
                  <ul>
                    <li>A supplier price moves between <strong>£6.39 and £7.99</strong> over three days. That is a 25% range — under 60%, so nothing happens and the listing stays on sale at its repriced value.</li>
                    <li>A supplier price is read as <strong>£6.96</strong> and then as <strong>£139.20</strong>. That is a range of about 1,900% — far over 60%, so the listing comes off sale rather than being republished at a price built on that reading.</li>
                  </ul>
                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Safe repricing is off until you turn it on.</strong> New stores start with it disabled; switching it on gives you 60% / 3 days / 1 day, which you can change at any time.
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: What happens when protection triggers</h2>
                  <p>
                    When the measured range goes over your threshold:
                  </p>
                  <ol>
                    <li>The eBay quantity is set to <strong>0</strong>, so the listing stays live but cannot be bought</li>
                    <li>In <strong>Products</strong> the row shows a <strong>Safe Repricing</strong> badge with the measured percentage, and the <strong>Safe Repricing</strong> filter under Out of Stock lists everything currently held back</li>
                    <li>Coderom keeps checking the product on its normal schedule</li>
                  </ol>
                  <p>
                    Putting it back on sale is automatic and needs nothing from you. Once the stability period has passed, Coderom measures the price range again — <strong>starting from the moment the listing came off sale</strong>, not over a fixed trailing window. If the price has not moved since then, the listing goes back on sale.
                  </p>
                  <p>
                    Measuring from that moment matters: if it looked back over the last few days instead, it would still see the very movement that took the listing off sale, and would take it off sale again within hours of restoring it.
                  </p>
                  <p>
                    Two things decide the quantity it comes back with:
                  </p>
                  <ul>
                    <li>The quantity is taken from the last figure Coderom worked out for that listing, so it matches what your stock settings would have set anyway. If the supplier is out of stock, that figure is 0 and the listing stays closed — it will not be reopened with stock that is not there.</li>
                    <li>The product must have been checked in the last 24 hours. If its data is older than that, restoring is postponed to the next round rather than done on stale figures.</li>
                  </ul>
                  <p>
                    A price that moved and then stayed at its new level does restore — at the new price. Coderom does not wait for it to return to what it was before.
                  </p>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that your pricing is protected, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/stock" className="next-step-card">
                      <div className="next-step-icon">📦</div>
                      <div className="next-step-content">
                        <h4>Stock synchronisation & inventory</h4>
                        <p>Keep your eBay listings in sync with Amazon stock levels 24/7</p>
                      </div>
                    </Link>
                    <Link to="/help/amazon-filters" className="next-step-card">
                      <div className="next-step-icon">🔍</div>
                      <div className="next-step-content">
                        <h4>Amazon supplier filters</h4>
                        <p>Set quality rules to automatically select the best Amazon offers</p>
                      </div>
                    </Link>
                    <Link to="/help/orders" className="next-step-card">
                      <div className="next-step-icon">📋</div>
                      <div className="next-step-content">
                        <h4>Order management & fulfilment</h4>
                        <p>Automatically process eBay orders and track shipments</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .threshold-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .threshold-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .threshold-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .threshold-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .threshold-table tbody tr:last-child td {
          border-bottom: none;
        }
        .threshold-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .threshold-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleSafeRepricing;
